import React from "react";
import {
  Box,
  Paper,
  Title,
  Stack,
  Text,
  Group,
  Grid,
  Checkbox,
  Alert,
  Button,
  Modal,
  Select,
  TextInput,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { type SignerEntry } from "../../lib/signing-metadata";
import { type SignatureAnnotation } from "../../types";
import { type KeyPair } from "../../lib/keys";
import { CreateKeyModal } from "../CreateKeyModal";

const SIGNER_COLORS = [
  "#228be6",
  "#40c057",
  "#fab005",
  "#fd7e14",
  "#e64980",
  "#be4bdb",
  "#7950f2",
];

type FinalizeStepProps = {
  metadata: { docTitle: string; author: string };
  numPages: number;
  annotations: SignatureAnnotation[];
  signers: SignerEntry[];
  updateSigner: (index: number, updates: Partial<SignerEntry>) => void;
  saving: boolean;
  onBack: () => void;
  onGenerateClick: () => void;

  // Modals state
  createKeyModalOpen: boolean;
  setCreateKeyModalOpen: (open: boolean) => void;
  handleKeyCreated: (key: KeyPair) => void;

  signingModalOpen: boolean;
  setSigningModalOpen: (open: boolean) => void;
  availableKeys: KeyPair[];
  selectedKeyId: string | null;
  setSelectedKeyId: (id: string | null) => void;
  passphrase: string;
  setPassphrase: (val: string) => void;
  handleConfirmSign: () => void;
};

export function FinalizeStep({
  metadata,
  numPages,
  annotations,
  signers,
  updateSigner,
  saving,
  onBack,
  onGenerateClick,
  createKeyModalOpen,
  setCreateKeyModalOpen,
  handleKeyCreated,
  signingModalOpen,
  setSigningModalOpen,
  availableKeys,
  selectedKeyId,
  setSelectedKeyId,
  passphrase,
  setPassphrase,
  handleConfirmSign,
}: FinalizeStepProps) {
  return (
    <Box
      p={{ base: "md", sm: 40 }}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box style={{ width: "100%", maxWidth: 800 }}>
        <Paper p={{ base: "md", sm: "xl" }} withBorder radius="md">
          <Group mb="lg">
            <ActionIcon variant="default" onClick={onBack}>
              ←
            </ActionIcon>
            <Title order={2}>Finalize Document</Title>
          </Group>

          <Stack gap="xl">
            <Box>
              <Text fw={700} mb="xs">
                Document Summary
              </Text>
              <Paper withBorder p="md" bg="gray.0" radius="sm">
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" c="dimmed">
                      Title
                    </Text>
                    <Text fw={500}>{metadata.docTitle}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" c="dimmed">
                      Total Pages
                    </Text>
                    <Text fw={500}>{numPages}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" c="dimmed">
                      Total Fields
                    </Text>
                    <Text fw={500}>{annotations.length}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" c="dimmed">
                      Signers
                    </Text>
                    <Text fw={500}>
                      {signers.filter((s) => s.email).length}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Paper>
            </Box>

            <Box>
              <Group justify="space-between" mb="xs">
                <Text fw={700}>Signer Requirements</Text>
                <Paper px="xs" py={2} bg="blue.0" withBorder radius="xl">
                  <Text size="10px" fw={700} c="blue.7">
                    STANDARD PLAN
                  </Text>
                </Paper>
              </Group>
              <Stack gap="md">
                {signers
                  .filter((s) => s.email)
                  .map((s, i) => (
                    <Paper key={i} withBorder p="md" radius="sm">
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <Box
                            w={12}
                            h={12}
                            bg={SIGNER_COLORS[i % SIGNER_COLORS.length]}
                            style={{ borderRadius: 2 }}
                          />
                          <Text fw={600}>{s.email}</Text>
                        </Group>
                      </Group>
                      <Stack gap="xs">
                        <Checkbox
                          label="Email must match exactly (Lock to Email)"
                          checked={!s.delegationAllowed}
                          onChange={(e) => {
                            const val = !e.currentTarget.checked;
                            updateSigner(i, {
                              delegationAllowed: val,
                              domainRestricted: val
                                ? s.domainRestricted
                                : false,
                            });
                          }}
                        />
                        {s.delegationAllowed && (
                          <Checkbox
                            ml="xl"
                            label="Alternate signee must use same email domain"
                            checked={s.domainRestricted}
                            onChange={(e) =>
                              updateSigner(i, {
                                domainRestricted: e.currentTarget.checked,
                              })
                            }
                          />
                        )}
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </Box>

            <Box style={{ opacity: 0.6 }}>
              <Group justify="space-between" mb="xs">
                <Text fw={700} c="dimmed">
                  Advanced Controls
                </Text>
                <Paper px="xs" py={2} bg="gray.1" withBorder radius="xl">
                  <Text size="10px" fw={700} c="dimmed">
                    PRO UPGRADE
                  </Text>
                </Paper>
              </Group>
              <Paper
                withBorder
                p="md"
                bg="gray.0"
                radius="sm"
                style={{ borderStyle: "dashed" }}
              >
                <Stack gap="sm">
                  <Checkbox
                    label="RFC 3161 Trusted Timestamp (eIDAS AES Qualification)"
                    disabled
                    description="Coming Soon"
                  />
                  <Divider variant="dotted" />
                  <Checkbox
                    label="Secondary Email OTP Identity Verification"
                    disabled
                    description="Coming Soon"
                  />
                  <Divider variant="dotted" />
                  <Checkbox
                    label="Blockchain Attestation"
                    disabled
                    description="Coming Soon"
                  />
                </Stack>
              </Paper>
            </Box>

            <Alert title="Creator Verification" color="blue">
              To finalize the document, you will need to cryptographically sign
              the metadata using your digital key. This ensures the document
              structure cannot be tampered with.
            </Alert>

            <Button
              fullWidth
              size="lg"
              onClick={onGenerateClick}
              loading={saving}
            >
              Sign & Generate PDF
            </Button>
          </Stack>
        </Paper>

        <CreateKeyModal
          opened={createKeyModalOpen}
          onClose={() => setCreateKeyModalOpen(false)}
          onCreated={handleKeyCreated}
        />

        <Modal
          opened={signingModalOpen}
          onClose={() => setSigningModalOpen(false)}
          title="Verify & Sign Document"
          centered
        >
          <Stack>
            <Stack>
              <Group align="flex-end">
                <Select
                  style={{ flex: 1 }}
                  label="Select Signing Key"
                  value={selectedKeyId}
                  onChange={setSelectedKeyId}
                  data={availableKeys.map((k) => ({
                    value: k.id,
                    label: `${k.name} (${k.email})`,
                  }))}
                />
                <Button
                  variant="default"
                  onClick={() => {
                    setSigningModalOpen(false);
                    setCreateKeyModalOpen(true);
                  }}
                >
                  +
                </Button>
              </Group>
              <TextInput
                label="Passphrase"
                description="Required if your key is encrypted"
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
              />
              <Button
                fullWidth
                size="md"
                onClick={handleConfirmSign}
                loading={saving}
                disabled={!selectedKeyId}
              >
                Confirm Signature
              </Button>
            </Stack>
          </Stack>
        </Modal>
      </Box>
    </Box>
  );
}
